import React, { useState, useEffect, useRef } from 'react';
import portalUploadService from '../../services/portalUploadService';
import { Upload, FileText, CheckCircle, XCircle, Clock } from 'lucide-react';
import useNotification from '../../hooks/useNotification';
import Notification from '../../components/common/Notification';

function PortalDocumentRequests() {
  const { notification, showError, hideNotification } = useNotification();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const [activeRequestId, setActiveRequestId] = useState(null);

  const loadRequests = async () => {
    try {
      const response = await portalUploadService.getDocumentRequests();
      setRequests(response.data.data || []);
    } catch (err) {
      console.error('Failed to load document requests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleUploadClick = (requestId) => {
    setActiveRequestId(requestId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeRequestId) return;

    setUploading(activeRequestId);
    try {
      await portalUploadService.uploadDocument(activeRequestId, file);
      await loadRequests();
    } catch (err) {
      console.error('Upload failed:', err);
      showError('Failed to upload document. Please try again.');
    } finally {
      setUploading(null);
      setActiveRequestId(null);
      e.target.value = '';
    }
  };

  const statusConfig = {
    pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Pending' },
    uploaded: { icon: Upload, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Uploaded' },
    accepted: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', label: 'Accepted' },
    rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Rejected' },
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Document Requests</h1>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {requests.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p>No document requests.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {requests.map((req) => {
              const config = statusConfig[req.status] || statusConfig.pending;
              const StatusIcon = config.icon;
              const isUploading = uploading === req.id;

              return (
                <div key={req.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{req.title}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
                          <StatusIcon className="h-3 w-3" />
                          {config.label}
                        </span>
                      </div>
                      {req.description && (
                        <p className="text-sm text-gray-600 mt-1">{req.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        {req.case_title && <span>{req.case_title}</span>}
                        {req.due_date && (
                          <span className={new Date(req.due_date) < new Date() && req.status === 'pending' ? 'text-red-600 font-medium' : ''}>
                            Due {new Date(req.due_date).toLocaleDateString()}
                          </span>
                        )}
                        {req.requested_by && <span>Requested by {req.requested_by}</span>}
                      </div>
                      {req.review_note && req.status !== 'pending' && (
                        <div className={`mt-2 text-xs p-2 rounded ${config.bg}`}>
                          <strong>Review note:</strong> {req.review_note}
                        </div>
                      )}
                    </div>

                    {req.status === 'pending' && (
                      <button
                        onClick={() => handleUploadClick(req.id)}
                        disabled={isUploading}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex-shrink-0 ml-3"
                      >
                        {isUploading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <Upload className="h-4 w-4" />
                        )}
                        {isUploading ? 'Uploading...' : 'Upload'}
                      </button>
                    )}

                    {req.status === 'rejected' && (
                      <button
                        onClick={() => handleUploadClick(req.id)}
                        disabled={isUploading}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 transition-colors flex-shrink-0 ml-3"
                      >
                        <Upload className="h-4 w-4" />
                        Re-upload
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Notification {...notification} onClose={hideNotification} />
    </div>
  );
}

export default PortalDocumentRequests;
